import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  MenuList,
  ListItemText,
  ListItemIcon,
  Switch,
} from '@mui/material';
import { DataGrid, GridColDef, GridCellParams, GridEventListener, GridRenderCellParams, GridToolbarQuickFilter, GridValueGetterParams } from '@mui/x-data-grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import SvgIcon from '@mui/material/SvgIcon';
import PublicIcon from '@mui/icons-material/Public';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import GridOnIcon from '@mui/icons-material/GridOn';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DataGridFilter from "../shared/components/DataGridFilter";
import AnalysisTypeColorPill from "../shared/components/AnalysisTypeColorPill";
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { stringToColor } from "../shared/utilities/Colors";
import { usePersistedState } from '../shared/utilities/utils';

const BoxPlotIcon = (props: any) => (
  <SvgIcon {...props}>
    <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2"/>
    <line x1="10" y1="20" x2="14" y2="20" stroke="currentColor" strokeWidth="1"/>
    <line x1="10" y1="4" x2="14" y2="4" stroke="currentColor" strokeWidth="1"/>
    <rect x="9" y="9" width="6" height="6" stroke="currentColor" fill="currentColor" strokeWidth="2"/>
  </SvgIcon>
);

const plotTypes = [
  { value: 'bar', label: 'Bar Chart', icon: BarChartIcon, color: '#14397e' },
  { value: 'scatter', label: 'Scatter Plot', icon: ScatterPlotIcon, color: '#3f71d1' },
  { value: 'box', label: 'Box Plot', icon: BoxPlotIcon, color: '#5fa6ce' }, 
  { value: 'heatmap', label: 'Heatmap', icon: GridOnIcon, color: '#85d8ce' }, 
  { value: 'line', label: 'Line Plot', icon: ShowChartIcon, color: '#E63946' }, 
];

const getPlotTypeIcon = (plotTypeValue: any, selected: boolean, color: string | null) => {
  const plotType = plotTypes.find((type) => type.value === plotTypeValue);
  const Icon = plotType?.icon;
  // return Icon ? <Icon sx={{ color: selected ? plotType.color : 'grey' }} /> : null;
  // return Icon ? <Icon sx={{ color: color }} /> : null;
  return Icon ? <Icon /> : null;
};

interface BrowseTabProps {
  plotGroups: PlotGroup[];
  openPlotOptionsModal: () => void;
  onPlotSelect: (plotIds: number[]) => void;
  onAddPlotGroup: () => void;
  onAddPlot: () => void;
  onDeletePlotGroup: (plotGroupId: number) => void;
  onDeletePlot: (plotId: number) => void;
  configurePlot: () => void;
  onRenamePlot: (plotId: number) => void;
  savePlot: () => void;
  selectedPlots: number[];
  updateSelectedPlots: (plotIds: number[]) => void;
  disableSave: boolean;
  isCompareMode: boolean;
  setIsCompareMode: (val: boolean) => void;
  versionTypes: versionType[];
  imageViewerIsOpen: boolean;
  toggleImageViewer: () => void;
}

interface versionType {
  id: number,
  version: number,
  analysis_type: analysisType,
  color: string | null;
}

interface analysisType {
  abbreviation: string,
  "full_name": string,
  "short_name": string,
}

const BrowseTab: React.FC<BrowseTabProps> = ({
  plotGroups,
  openPlotOptionsModal,
  onPlotSelect,
  onAddPlotGroup,
  onAddPlot,
  onDeletePlotGroup,
  onDeletePlot,
  onRenamePlot,
  configurePlot,
  savePlot,
  selectedPlots,
  updateSelectedPlots,
  disableSave,
  isCompareMode,
  setIsCompareMode,
  versionTypes,
  imageViewerIsOpen,
  toggleImageViewer
}) => {

  const [plotActionsMenuAnchorEl, setPlotActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [rowAnchor, setRowAnchor] = useState<number | null>(null);
  // const [selectedAnalysisTypeFilter, setSelectedAnalysisTypeFilter] = useState<number[]>(() => {
  //   const savedAnalysisTypeFilter = localStorage.getItem('browseTab_analysisTypeFilter');
  //   return (savedAnalysisTypeFilter && versionTypes.includes(JSON.parse(savedAnalysisTypeFilter)[0])) ? JSON.parse(savedAnalysisTypeFilter) : [];
  // });
  const [selectedAnalysisTypeFilter, setSelectedAnalysisTypeFilter] = usePersistedState<number[]>('browseTab_analysisTypeFilter', []);

  useEffect(() => {
    // If the selected analysis type filter contains types that are no longer available, remove them
    const validFilter = selectedAnalysisTypeFilter.filter(typeId => versionTypes.map(type => type.id).includes(typeId));
    if (validFilter.length !== selectedAnalysisTypeFilter.length) {
      setSelectedAnalysisTypeFilter(validFilter);
    }
  }, [versionTypes]);

  const filteredPlots = useMemo(() => {
    const allPlots = plotGroups.flatMap(group => group.plots);
    return allPlots.filter(plot => {
      if (selectedAnalysisTypeFilter.length === 0) return true;
      if (plot.version_types.length == 0) return false;
      return plot.version_types.every(vt => selectedAnalysisTypeFilter.includes(vt));
    });
  }, [plotGroups, selectedAnalysisTypeFilter]);

  // console.log("filteredPlots: ", filteredPlots);

  useEffect(() => {
    if (filteredPlots.length === 0) return;

    // Don't update if the selected plot is already in the filtered plots
    if (filteredPlots.map(plot => plot.id).includes(selectedPlots[0])) return;

    updateSelectedPlots([filteredPlots[0].id]);
    
  }, [filteredPlots, selectedPlots]);

  const handleSelectPlots = (plotIds: number[]) => {
    // localStorage.setItem('selectedPlotIds', JSON.stringify(plotIds));
    // updateSelectedPlots(plotIds);
    onPlotSelect(plotIds);
    // console.log("Browse Tab: OnPlotSelect: ", plotIds);
  };

  const handleAnalysisTypeFilter = (filteredAnalysisTypes: number[]) => {
    setSelectedAnalysisTypeFilter(filteredAnalysisTypes);
    // localStorage.setItem('browseTab_analysisTypeFilter', JSON.stringify(filteredAnalysisTypes));
  }

  const handlePlotActionsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, rowId: number) => {
    event.stopPropagation();
    setPlotActionsMenuAnchorEl(event.currentTarget);
    setRowAnchor(rowId);
  };

  const handlePlotActionsMenuClose = () => {
    setPlotActionsMenuAnchorEl(null);
    setRowAnchor(null);
  };

  const handleRowSelection = (newSelectionModel: any) => {
    const selectedIds = newSelectionModel.map((id: any) => Number(id));
    if (!isCompareMode && selectedIds.length > 1) {
      // In single-select mode, keep only the last selected plot
      const lastSelected = selectedIds[selectedIds.length - 1];
      handleSelectPlots([lastSelected]);
    } else {
      handleSelectPlots(selectedIds);
    }
  };

  const handleKeyDown: GridEventListener<'cellKeyDown'> = (
    params, // GridRowParams
    event, // MuiEvent<React.MouseEvent<HTMLElement>>
  ) => {

    if (!isCompareMode) {
      const gridRowIds = filteredPlots.map((plot) => ({id: plot.id,}));
      const currentIndex = gridRowIds.findIndex((row) => row.id === params.row.id);
      
      if (event.key === "ArrowDown" && currentIndex < gridRowIds.length - 1) {
        const nextPlotId = gridRowIds[currentIndex + 1].id;
        handleSelectPlots([nextPlotId]);
      }

      if (event.key === "ArrowUp" && currentIndex > 0) {
        const previousPlotId = gridRowIds[currentIndex - 1].id;
        handleSelectPlots([previousPlotId]);
      }
    } 
  };

  const CustomToolbar: React.FC = () => {
    return (
      <Box 
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        // alignItems="center"
        sx={{marginBottom: 2, marginTop: 2}}
      >
        <GridToolbarQuickFilter sx={{ margin: 1.1 }} />
        <Box flexGrow={1} sx={{marginRight: 5}}>
          <DataGridFilter
            id_name="analysis-type-filter"
            placeholder="Filter by Analysis Type"
            items = {versionTypes.filter(type => filteredPlots.map(plot => plot.version_types[0]).includes(type.id)).map(type => (
              {
                id: type.id,
                label: type.analysis_type.short_name + " " + String(type.version % 1 === 0 ? `${type.version}.0` : type.version),
                color: (type && type.color) ? type.color : stringToColor((type as versionType)?.analysis_type?.abbreviation || '') 
              }))}
            value={selectedAnalysisTypeFilter}
            handleCustomFilter={handleAnalysisTypeFilter}
            pillColor="#b6e8fc"
            width="100%"
          />
        </Box>
        {/* <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Compare  <i>(Beta)</i>
          </Typography>
          <Switch
            checked={isCompareMode}
            onChange={handleCompareModeChange}
            name="compareMode"
            size="small"
          />
        </Box> */}
      </Box>
    );
  }

  const columns: GridColDef[] = [
    {
      field: 'plotType',
      headerName: 'Plot Type',
      width: 100,
      // Use valueGetter to return the full_name for sorting
      valueGetter: (params: GridValueGetterParams) => {
        const versionType = versionTypes.find(type => type.id === params.row.version_types[0]);
        return versionType?.analysis_type?.full_name || '';
      },
      renderCell: (params: GridCellParams) => {
        const plotType = params.row.settings?.plotType;
        const selected = selectedPlots.includes(params.row.id as number);
        const versionType = versionTypes.find(type => type.id === params.row.version_types[0]);
        const color = (versionType && versionType.color) ? versionType.color : stringToColor((versionType as versionType)?.analysis_type?.abbreviation || '');

        return (
          <Tooltip title={versionType ? versionType.analysis_type.full_name : ""}>
            <IconButton
              size='small'
              onClick={() => handleRowSelection([params.row.id as number])}
              sx={{
                color: color,
                outline: 'none',
                border: 'none',
                boxShadow: 'none',
                padding: 0,
                margin: 0,
                '&:hover': {
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  backgroundColor: 'transparent'
                },
                '&:focus': {
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none'
                },
              }}
            >
              {getPlotTypeIcon(plotType, selected, color)}
            </IconButton>
          </Tooltip>

        );
      },
      // Sort based on the analysis_type.full_name value
      sortComparator: (v1, v2) => v1.localeCompare(v2),
    },
    {
      field: 'name',
      headerName: 'Plot Name',
      flex: 2,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'actions',
      headerName: '',
      flex: 1,
      align: 'right',
      renderCell: (params: GridCellParams) => {
        if (params.row.addNew) {
          return null;
        }
        
        const isPublic = params.row.public;

        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
            {isPublic &&
              <Tooltip title={isPublic ? "This plot is public and cannot be modified" : ""}>              
                <PublicIcon
                  color="disabled"
                  sx={{ fontSize: 16, marginLeft: '35px', marginRight:'10px' }}
                />
              </Tooltip>
            }
            <span>
            {!isPublic && 
              <Tooltip title="Actions">
                <IconButton
                size="small"
                onClick={(event) => handlePlotActionsMenuOpen(event, params.row.id as number)}
              >
                <MoreVertIcon />
              </IconButton>
              </Tooltip>
            }
            </span>
      
            <Menu
              id={`plot-actions-menu ${params.row.id}`}
              anchorEl={plotActionsMenuAnchorEl}
              open={Boolean(plotActionsMenuAnchorEl)}
              onClose={handlePlotActionsMenuClose}
              sx={{p:'2px', m:'0'}}
              MenuListProps={{
                'aria-labelledby': 'lock-button',
                role: 'listbox',
              }}
            >              
              <MenuList>
                <MenuItem
                  dense
                  onClick={(event) => {
                    event.stopPropagation();
                    if (rowAnchor) 
                      onDeletePlot(rowAnchor);
                    handlePlotActionsMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon />
                  </ListItemIcon>
                  <ListItemText primary="Delete" />
                </MenuItem>
                <MenuItem dense onClick={configurePlot}>
                  <ListItemIcon>
                    <PermDataSettingIcon />
                  </ListItemIcon>
                  <ListItemText>Configure</ListItemText>
                </MenuItem>
                <MenuItem dense onClick={(event => {
                  event.stopPropagation();
                  if (rowAnchor)
                    onRenamePlot(rowAnchor);
                  handlePlotActionsMenuClose();
                })}>
                  <ListItemIcon>
                    <DriveFileRenameOutlineIcon />
                  </ListItemIcon>
                  <ListItemText>Rename</ListItemText>
                </MenuItem>
              </MenuList>
            </Menu>
          </Box> 
        )},
    },
    {
      field: 'searchableLabels',
      headerName: 'Searchable Labels',
      // hide: true,  // Hide this column but make it searchable
    },
  ];

  return (
    <Box maxHeight={'80vh'} display={'flex'} flexDirection={'column'} overflow= {'none'}>
      {(filteredPlots.length > 0 && filteredPlots.map(plot => plot.id).includes(selectedPlots[0])) ? (
        <>
          <DataGrid
            rows={filteredPlots.map((plot) => ({
              id: plot.id,
              name: plot.name,
              group: plot.plot_group,
              settings: plot.settings,
              public: plot.public,
              plot_group: plot.plot_group,
              version_types: plot.version_types,
              searchableLabels: plot.version_types.map((id: number) => {
                const versionType = versionTypes.find(type => type.id === id);
                return versionType?.analysis_type.abbreviation || '';
              }).join(' '),  // Add searchable labels from abbreviations
            }))}
            columns={columns}
            // autoHeight
            hideFooter
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            density="compact"
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#e5ecf6', 
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: '#b3d4fc', 
              },
              '& .MuiDataGrid-row.Mui-selected:hover': {
                backgroundColor: '#b3d4fc', 
              },
              '& .MuiDataGridRow:overflowY':'scroll',
            }}
            // columnHeaderHeight={0}
            slots={{
              toolbar: CustomToolbar,
            }}
            onRowSelectionModelChange={(model: any) => {handleRowSelection(model);}}
            rowSelectionModel={selectedPlots}
            checkboxSelection={isCompareMode}
            onCellKeyDown={handleKeyDown}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  searchableLabels: false,
                },
              },
            }}
          />
          <Box sx={{height: "100%", display: "flex", alignItems: "flex-end"}}>
            <Button
              variant="text"
              startIcon={<AddCircleOutlineIcon />}
              onClick={onAddPlot}
              sx={{
                marginTop: 1,
                color: 'grey',
                borderColor: 'grey',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'grey',
                  backgroundColor: 'transparent',
                },
                maxHeight: 30,
                maxWidth: 100
              }}
            >
              New Plot
            </Button>

            <Button
              variant="text"
              startIcon={(imageViewerIsOpen) ? <RemoveCircleOutline/> : <AddCircleOutlineIcon />}
              onClick={toggleImageViewer}
              sx={{
                marginTop: 1,
                color: 'grey',
                borderColor: 'grey',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'grey',
                  backgroundColor: 'transparent',
                },
                maxHeight: 30,
                maxWidth: 150
              }}
            >
              {(imageViewerIsOpen) ? "Close Images": "Open Images"}
            </Button>
          </Box>
        </>
      ) : (
        <div></div>
      )}
    </Box>
  );
}

export default BrowseTab;